import {
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  IconButtonSection,
  IconSizeEnum,
  ModalDialog,
  ModalMessage,
  ModalMessageColorsEnum,
  PlusIcon,
  Typography,
} from '@acceso/design-system';
import { useTranslation } from 'react-i18next';

import { TableWithDetailCard } from '@/components/TableWithDetailCard/TableWithDetailCard';
import { FormTypeEnum } from '@/types';
import { getParamsFromTableFilters } from '@/utils/table-filters';

import { useProductionCenters } from '../api/get-production-centers';
import { usePatchProductionCenter } from '../api/patch-production-center';
import { usePostProductionCenter } from '../api/post-production-center';
import { ProductionCenterDetailCard } from '../components/ProductionCenterDetailCard';
import { ProductionCenterDrawerForm } from '../components/ProductionCenterDrawerForm';
import { ProductionCenterTable } from '../components/ProductionCenterTable';
import { useProductionCenterControllers } from '../hooks/useProductionCenterControllers';
import { useProductionCenterPermissions } from '../hooks/useProductionCenterPermissions';
import { useProductionCenterFilters } from '../stores/production-center-filters';
import { ProductionCenterType } from '../types';

export default function ProductionCenterPage() {
  const { t } = useTranslation();

  const { filters, paginationValues } = useProductionCenterFilters();
  const {
    data: { data: productionCenters = [], total = 0 } = {},
    isFetching,
    isError,
  } = useProductionCenters({
    params: getParamsFromTableFilters({
      filters,
      paginationValues,
    }),
  });

  const { productionCenterDrawer, productionCenterDialog, handleDeleteProductionCenter } =
    useProductionCenterControllers();

  const { mutate: createProductionCenter, isPending: isCreatingProductionCenter } =
    usePostProductionCenter();

  const { mutate: updateProductionCenter, isPending: isUpdatingProductionCenter } =
    usePatchProductionCenter();

  const { canCreate, canUpdate, canDuplicate, canDelete } = useProductionCenterPermissions();

  const onCreate = () => {
    if (!canCreate) return;
    productionCenterDrawer.open({ type: FormTypeEnum.CREATE, data: null });
  };

  const onUpdate = (productionCenter: ProductionCenterType) => {
    if (!canUpdate) return;
    productionCenterDrawer.open({ type: FormTypeEnum.UPDATE, data: productionCenter });
  };

  const onDuplicate = (productionCenter: ProductionCenterType) => {
    if (!canDuplicate) return;
    productionCenterDrawer.open({ type: FormTypeEnum.DUPLICATE, data: productionCenter });
  };

  const onDelete = (productionCenter: ProductionCenterType) => {
    if (!canDelete) return;
    productionCenterDialog.open(productionCenter);
  };

  return (
    <>
      <div className="flex flex-col">
        <div className="flex justify-between py-size-8">
          <Typography
            fontSize={FontSizeEnum.Xl}
            weight={FontWeightEnum.Bold}
            color={FontColorEnum.Dark10}
          >
            {t('Centros de producción')}
          </Typography>

          {canCreate && (
            <IconButtonSection
              className="mr-size-16"
              onClick={onCreate}
              internalProps={{
                icon: {
                  size: IconSizeEnum.SM,
                  type: PlusIcon,
                },
                text: {
                  value: t('Nuevo'),
                  color: FontColorEnum.Default,
                },
              }}
            />
          )}
        </div>

        <TableWithDetailCard<ProductionCenterType>
          isError={isError}
          renderTable={({ setData, handleOpenSecondCard, data }) => (
            <ProductionCenterTable
              items={productionCenters}
              totalItems={total}
              activeItem={data}
              onUpdate={canUpdate ? onUpdate : undefined}
              onDuplicate={canDuplicate ? onDuplicate : undefined}
              onDelete={canDelete ? onDelete : undefined}
              onSelect={handleOpenSecondCard}
              setSelectedUser={setData}
              isFetching={isFetching}
            />
          )}
          renderDetailCard={({ data, isSecondCardCollapsed, handleSecondCardCollapse }) => (
            <ProductionCenterDetailCard
              productionCenter={data}
              isCollapsed={isSecondCardCollapsed}
              onCollapse={handleSecondCardCollapse}
              onUpdate={onUpdate}
            />
          )}
        />
      </div>

      <ProductionCenterDrawerForm
        isOpen={productionCenterDrawer.isOpen}
        onClose={productionCenterDrawer.close}
        onSubmit={data => {
          if (productionCenterDrawer.data?.type === FormTypeEnum.UPDATE) {
            updateProductionCenter(
              {
                ...data,
                id: productionCenterDrawer.data.data.id,
              },
              {
                onSuccess: () => {
                  productionCenterDrawer.close();
                },
              }
            );
          } else {
            createProductionCenter(data, {
              onSuccess: () => {
                productionCenterDrawer.close();
              },
            });
          }
        }}
        isMutating={isCreatingProductionCenter || isUpdatingProductionCenter}
        data={productionCenterDrawer.data || undefined}
      />

      <ModalDialog
        isOpen={productionCenterDialog.isOpen}
        onClose={productionCenterDialog.close}
        onAction={handleDeleteProductionCenter}
        heading={t('Eliminar centro de producción')}
        actionLabel={t('Eliminar')}
        linkLabel={t('Cancelar')}
        color={ModalMessageColorsEnum.ERROR}
      >
        <ModalMessage
          title={t('¿Estás seguro de querer eliminar el centro de producción seleccionado?')}
        />
      </ModalDialog>
    </>
  );
}
